import {
  AssetConfigAudio,
  AssetConfigImages,
  AssetConfigMultiAtlas,
  AssetConfigSpine,
  AssetConfigSpriteSheet,
  AssetManifestPixi,
  AudioDataset,
  BotPartSlot,
  IAssetsBundle,
  IMaDataset,
  ISsDataset,
  SpineDataset,
  SwatchDataByName,
  UResourceType
} from '@nova/types';
import { ReadonlyDeep } from 'type-fest';

import {
  assetConfigAudioLiteral,
  assetConfigImageLiteral,
  assetConfigMultiAtlasLiteral,
  assetConfigSpineLiteral,
  assetConfigSpriteSheetLiteral,
  assetManifestPixiLiteral,
  configSwatchLiteral
} from '../configs';

// Images
assetConfigImageLiteral satisfies AssetConfigImages;
type AssetConfigImageLiteral = typeof assetConfigImageLiteral;
export type AssetConfigImageKeys = keyof AssetConfigImageLiteral;
export function isKeyOfImageConfig(str: string): str is AssetConfigImageKeys {
  return str in assetConfigImageLiteral;
}

// Spine
assetConfigSpineLiteral satisfies AssetConfigSpine;
type AssetConfigSpineLiteral = typeof assetConfigSpineLiteral;
export type AssetConfigSpineKeys = keyof AssetConfigSpineLiteral;

// Multi-Atlases
assetConfigMultiAtlasLiteral satisfies AssetConfigMultiAtlas;
type AssetConfigMultiAtlasLiteral = typeof assetConfigMultiAtlasLiteral;
export type AssetConfigMultiAtlasKeys = keyof AssetConfigMultiAtlasLiteral;

// Sritesheets
assetConfigSpriteSheetLiteral satisfies AssetConfigSpriteSheet;
type AssetConfigSpriteSheetLiteral = typeof assetConfigSpriteSheetLiteral;
export type AssetConfigSpriteSheetKeys = keyof AssetConfigSpriteSheetLiteral;

// Pixi manifest
assetManifestPixiLiteral satisfies ReadonlyDeep<AssetManifestPixi>;
type AssetManifestPixiLiteral = typeof assetManifestPixiLiteral;
export type AssetManifestPixiKeys = keyof AssetManifestPixiLiteral;

// Audio
assetConfigAudioLiteral satisfies AssetConfigAudio;
type AssetConfigAudioLiteral = typeof assetConfigAudioLiteral;
export type AssetConfigAudioTracksKeys = keyof AssetConfigAudioLiteral;

// Swatches
configSwatchLiteral satisfies ReadonlyDeep<SwatchDataByName>;
type ConfigSwatchLiteral = typeof configSwatchLiteral;
export type ConfigSwatchKeys = keyof ConfigSwatchLiteral;
export function isKeyOfSwatchConfig(str: string): str is ConfigSwatchKeys {
  return str in configSwatchLiteral;
}

/** Config swatches, none, or app-specific keys (e.g. imported swatch ids). */
export type SwatchSelection = ConfigSwatchKeys | 'swatch_none' | string;
export const SWATCH_NONE: SwatchSelection = 'swatch_none';
export type BotSwatchBySlot = Record<BotPartSlot, SwatchSelection>;
export type PartialBotSwatchBySlot = Partial<Record<BotPartSlot, SwatchSelection>>;

// ! These are the ResourceType enum values, not it's keys. Change the key & value of "image" in the enum and see how & when the error comes up.
export type ResourceTypeToConfigKeyMap = {
  [Key in UResourceType]: {
    ['image']: AssetConfigImageKeys;
    ['pixiManifest']: AssetManifestPixiKeys;
    ['spineAtlas']: AssetConfigSpineKeys;
    ['spritesheet']: AssetConfigSpriteSheetKeys;
    ['multiAtlas']: AssetConfigMultiAtlasKeys;
    ['audioTrack']: AssetConfigAudioTracksKeys;
  }[Key];
};

export type ResourceTypeToConfigMap = {
  [Key in UResourceType]: {
    ['image']: AssetConfigImages;
    ['pixiManifest']: AssetManifestPixi;
    ['spineAtlas']: AssetConfigSpine;
    ['spritesheet']: AssetConfigSpriteSheet;
    ['multiAtlas']: AssetConfigMultiAtlas;
    ['audioTrack']: AssetConfigAudio;
  }[Key];
};

export type ResourceTypeToConfigLiteralMap = {
  [Key in UResourceType]: {
    ['image']: AssetConfigImageLiteral;
    ['pixiManifest']: AssetManifestPixiLiteral;
    ['spineAtlas']: AssetConfigSpineLiteral;
    ['spritesheet']: AssetConfigSpriteSheetLiteral;
    ['multiAtlas']: AssetConfigMultiAtlasLiteral;
    ['audioTrack']: AssetConfigAudioLiteral;
  }[Key];
};

export type ResourceTypeToConfigItemMap = {
  [Key in UResourceType]: {
    ['image']: string;
    // This one is utter nonsense, because it has the single key "bundles" pre-built into it's config structure.
    ['pixiManifest']: IAssetsBundle[];
    ['spineAtlas']: SpineDataset;
    ['spritesheet']: ISsDataset;
    ['multiAtlas']: IMaDataset;
    ['audioTrack']: AudioDataset;
  }[Key];
};

// Other
export type AssetSetLiteral = { [TKey in UResourceType]: ResourceTypeToConfigLiteralMap[TKey] };
export type AssetSet = { [TKey in UResourceType]: ResourceTypeToConfigMap[TKey] };
